import React from 'react';
import { Briefcase, TrendingUp, Users, Award } from 'lucide-react';

const Experience = () => {
  const achievements = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Improved Client Branding",
      description: "Enhanced online presence for multiple businesses through modern UI/UX design"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Client Satisfaction",
      description: "Delivered responsive, SEO-optimized websites that exceeded client expectations"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Performance Optimization",
      description: "Implemented advanced optimization techniques for better user experience"
    }
  ];

  return (
    <section id="experience" className="py-20 bg-gray-900">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Professional <span className="text-cyan-400">Experience</span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-purple-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Real-world experience delivering quality solutions to businesses
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Experience Description */}
            <div className="space-y-8">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
                <div className="flex items-center mb-6">
                  <div className="p-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg mr-4">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Freelance Web Developer</h3>
                    <p className="text-cyan-400 font-medium">2023 - Present</p>
                  </div>
                </div>
                
                <div className="prose prose-lg text-gray-300">
                  <p className="text-lg leading-relaxed mb-4">
                    Successfully delivered responsive, SEO-optimized websites for various businesses, 
                    focusing on modern UI/UX design and performance optimization.
                  </p>
                  
                  <p className="text-lg leading-relaxed mb-6">
                    Worked closely with clients including <span className="text-cyan-400 font-semibold">Ibrahim Dairies</span> and 
                    <span className="text-purple-400 font-semibold"> HS Indents</span>, improving their online visibility 
                    and brand presence through custom web solutions.
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="text-white font-semibold text-lg mb-4">Key Responsibilities:</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-300">Designed and developed responsive websites using modern web technologies</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-300">Implemented SEO best practices to improve search engine visibility</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-pink-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-300">Collaborated with clients to understand requirements and deliver solutions</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-300">Optimized website performance for better user experience</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right Side - Achievements */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white mb-8">Key Achievements</h3>
              
              {achievements.map((achievement, index) => (
                <div 
                  key={index}
                  className="group bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 hover:border-cyan-400/50 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg group-hover:scale-110 transition-transform duration-300">
                      <div className="text-white">
                        {achievement.icon}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-lg mb-2">
                        {achievement.title}
                      </h4>
                      <p className="text-gray-400">
                        {achievement.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Skills Used */}
          <div className="mt-16 bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              Technologies Used in Freelance Projects
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {["HTML", "CSS", "JavaScript", "React", "Node.js", "SEO"].map((tech, index) => (
                <div 
                  key={index}
                  className="text-center p-4 bg-gray-900/50 rounded-lg hover:bg-gray-900/70 transition-colors duration-300"
                >
                  <div className="text-cyan-400 font-semibold">{tech}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;